/** @file pictureWorldPoint: a world point moved to where the drawn picture shows it (rounded, then snapped). */
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { screenOf } from '@zilo/pose3d-render-core/camera/screenOf';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import { snapRelOf } from '@zilo/pose3d-render-core/place/snapRelOf';
import type { V3 } from './matrixRows';
import { pictureRound } from './pictureRound';

/**
 * Writes into `out` (world, z up) the entity-relative point `p` as the picture places a drawable's point A (21.4,
 * `impostorFieldsOf`): rounded once on the profile's world grid (`snapRelOf`), projected to target px (`screenOf`),
 * moved within the view plane onto `pictureRound.point(s, f)` with `(fx, fy)` the pivot fraction of the part drawn
 * there. Its view depth is kept. Without `snap 'pixel'` the point is returned as it is (the picture is not snapped).
 */
export function pictureWorldPoint(out: V3, p: Vec3, view: View, fx: number, fy: number): void {
  const r = snapRelOf(p, view);
  [out[0], out[1], out[2]] = [r[0], r[1], r[2]];
  if (!view.snap) return;
  const [sx, sy] = screenOf(r, view);
  const a = (pictureRound.point(sx, fx) - sx) / view.pixelsPerUnit;
  const b = (pictureRound.point(sy, fy) - sy) / view.pixelsPerUnit;
  const { right: R, up: U } = view.basis;
  out[0] += R[0] * a - U[0] * b;
  out[1] += R[1] * a - U[1] * b;
  out[2] += (R[2] * a - U[2] * b) / view.zScaleView;
}
